// src/ChangePasswordPage.js – password + language preference
import React, { useEffect, useState } from 'react';
import HintPopup from './HintPopup';
import NotificationPopup from './NotificationPopup';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import { applyLanguage } from './i18n';

const API_URL = '';

export default function ChangePasswordPage() {
  const { t } = useTranslation('profile');
  const { t: tc } = useTranslation('common');
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  async function checkAuth() {
    try {
      const res = await fetch('/auth/me', { credentials: 'include', cache: 'no-store' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!res.ok || !ct.includes('application/json')) return null;
      const j = await res.json().catch(() => null);
      if (j?.ok) return j.user || null;
      return null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      setUser(u);
      if (u?.preferredLanguage) await applyLanguage(u.preferredLanguage);
      setReady(true);
    })();
  }, []);

  useEffect(() => {
    if (ready && !user) {
      const next = window.location.pathname + window.location.search;
      window.location.replace('/login?next=' + encodeURIComponent(next || '/'));
    }
  }, [ready, user]);

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg('');
    if (!currentPassword || !newPassword) {
      setMsg(t('requiredBoth'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setMsg(t('mismatch'));
      return;
    }
    if (newPassword.length < 8) {
      setMsg(t('tooShort'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        currentPassword,
        newPassword
      }, { withCredentials: true });
      setMsg(t('success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMsg(err.response?.data?.error || t('failed'));
    } finally {
      setLoading(false);
    }
  }

  if (!ready) {
    return (
      <div className="sft-panel">
        <div className="sft-hint">{tc('checkingAuth')}</div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="sft-panel">
      <h2 style={{ margin: '0 0 12px' }}>
        {t('changePasswordTitle')}{' '}
        <HintPopup text={t('changePasswordHint')} placement="right" />
      </h2>
      {msg && <NotificationPopup message={msg} onClose={() => setMsg('')} />}

      <div className="sft-card" style={{ padding: 16, marginBottom: 20, maxWidth: 400 }}>
        <label style={{ display: 'block', marginBottom: 8 }}>{t('language')}</label>
        <p className="sft-hint" style={{ marginTop: 0 }}>{t('languageHint')}</p>
        <LanguageSelector
          user={user}
          onUserLanguageChange={(lang) => setUser((u) => (u ? { ...u, preferredLanguage: lang } : u))}
        />
      </div>

      <form onSubmit={handleSubmit} className="sft-form-narrow" style={{ maxWidth: 400 }}>
        <label style={{ display: 'block', marginBottom: 8 }}>{t('currentPassword')}</label>
        <input
          type="password"
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
          className="sft-input"
          style={{ width: '100%', marginBottom: 12 }}
          autoComplete="current-password"
        />
        <label style={{ display: 'block', marginBottom: 8 }}>{t('newPassword')}</label>
        <input
          type="password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          className="sft-input"
          style={{ width: '100%', marginBottom: 12 }}
          autoComplete="new-password"
        />
        <label style={{ display: 'block', marginBottom: 8 }}>{t('confirmPassword')}</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          className="sft-input"
          style={{ width: '100%', marginBottom: 16 }}
          autoComplete="new-password"
        />
        <button type="submit" className="sft-btn primary" disabled={loading}>{t('submit')}</button>
      </form>
    </div>
  );
}
